// src/hooks/useHandleGoogleSignIn.ts
import { Capacitor } from '@capacitor/core'
import { SocialLogin } from '@capgo/capacitor-social-login'
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  fetchSignInMethodsForEmail,
  linkWithCredential
} from 'firebase/auth'
import fetchWithAuth from '../utils/fetchWithAuth'

export const useHandleGoogleSignIn = () => {
  const auth = getAuth()
  const isNative = Capacitor.getPlatform() !== 'web'

  return async () => {
    let idToken: string

    // 1) Get the Google ID token (native or web)
    if (isNative) {
      const res: any = await SocialLogin.login({ provider: 'google' } as any);
      console.log('[Capgo] login payload:', res)
      idToken = (res.result as any).idToken
      if (!idToken) throw new Error('Google login did not return an idToken')
    } else {
      const provider = new GoogleAuthProvider()
      const result   = await import('firebase/auth').then(m => m.signInWithPopup(auth, provider))
      idToken        = await result.user.getIdToken()
    }

    const googleCred = GoogleAuthProvider.credential(idToken)

    try {
      // 2) Try to sign in with Google
      await signInWithCredential(auth, googleCred)
    } catch (e: any) {
      if (e.code === 'auth/account-exists-with-different-credential') {
        const email   = e.customData.email as string
        const methods = await fetchSignInMethodsForEmail(auth, email)

        // If that email already lives on Apple…
        if (methods.includes('apple.com')) {
          // a) trigger the Apple flow to get an Apple credential
          const appleRes = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email'] } })
          const appleIdToken = (appleRes.result as any).idToken
          const appleCred = GoogleAuthProvider.credential(appleIdToken)

          // b) SIGN IN with Apple first (so auth.currentUser is set)
          await signInWithCredential(auth, appleCred)

          // c) LINK the Google credential into that same account
          await linkWithCredential(auth.currentUser!, googleCred)
        } else {
          // handle other methods (e.g. password) if you like
          throw e
        }
      } else {
        throw e
      }
    }

    // 3) Finally sync with your backend
    await fetchWithAuth(`/api/users/${auth.currentUser!.uid}`)
  }
}